'use client';
import { useSearchParams } from 'next/navigation';
import { Suspense, useState, useMemo } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import { createClient } from '@/lib/supabase/client';
import { trackEvent } from '@/lib/analytics';

// ── Types ──────────────────────────────────────────────────────────────────
interface Resource {
  id: string;
  title: string;
  category: 'Strategy Templates' | 'Tech Modules' | 'DIY Guides' | 'Legal Templates';
  format: 'PDF' | 'Google Sheets' | 'Google Docs';
  description: string;
  included: string[];
  // TODO: Replace with real file URLs when assets are ready
  fileUrl: string | null;
}

type FilterCategory = 'All' | 'Strategy Templates' | 'Tech Modules' | 'DIY Guides' | 'Legal Templates';

// ── Resource Data ──────────────────────────────────────────────────────────
const RESOURCES: Resource[] = [
  {
    id: '90-day-marketing-roadmap',
    title: '90-Day Marketing Roadmap Template',
    category: 'Strategy Templates',
    format: 'Google Sheets',
    description:
      'A structured 13-week sprint planner built for founders and lean marketing teams who need to move fast without losing focus. Each week is broken into execution columns across paid, organic, content, and email channels so nothing falls through the cracks. Built-in KPI tracking rows let you set targets at the start of each sprint and measure actuals in real time. A weekly review cadence is baked in so you can course-correct before the quarter slips away.',
    included: [
      'Week-by-week task planner',
      'Channel-specific columns (paid / organic / content / email)',
      'KPI target vs. actual tracking',
      'Sprint retrospective prompts',
      '90-day goal worksheet',
    ],
  },
  {
    id: 'brand-positioning-worksheet',
    title: 'Brand Positioning Worksheet',
    category: 'Strategy Templates',
    format: 'PDF',
    description:
      'A structured framework for defining your brand\'s unique market position before you spend a dollar on advertising. Walk through your ideal customer profile, map your competitive differentiation, and lock in a value proposition that actually resonates. The worksheet guides you from vague brand feelings to a single, ownable positioning statement you can hand to any copywriter or agency. Includes a brand voice and tone guide so every piece of content sounds like you.',
    included: [
      'Ideal customer profile builder',
      'Competitive differentiation matrix',
      'Brand voice and tone guide',
      'Value proposition canvas',
      'One-sentence positioning statement formula',
    ],
  },
  {
    id: 'content-calendar-template',
    title: 'Content Calendar Template',
    category: 'Strategy Templates',
    format: 'Google Sheets',
    description:
      'A monthly content planning system that covers every major channel in one place — no more juggling separate docs for Instagram, LinkedIn, email, and TikTok. Each channel has its own tab with post type, caption draft, hashtag planner, and publish date columns. Monthly and weekly calendar views give you both the big picture and the day-to-day execution view. Content pillar tracking ensures you\'re hitting the right mix of educational, promotional, and engagement content.',
    included: [
      'Monthly and weekly calendar views',
      'Channel tabs (Instagram / LinkedIn / TikTok / Email)',
      'Caption and hashtag planner',
      'Content pillar tracker',
      'Posting frequency recommendations',
    ],
  },
  {
    id: 'autopilot-starter-kit',
    title: 'AutoPilot Starter Kit',
    category: 'Tech Modules',
    format: 'PDF',
    description:
      'A step-by-step guide to setting up your own AI-assisted marketing stack using free and low-cost tools — no agency required. We break down exactly which tools to use at each budget tier, how to connect them, and how to automate the repetitive work that eats your week. Includes the same workflow diagrams we use internally to onboard new AutoPilot clients, adapted for self-service setup. A weekly self-audit checklist keeps your stack running clean after launch.',
    included: [
      'Recommended tool stack by budget tier',
      'AI prompt templates for content creation',
      'Automation workflow diagrams',
      'Platform setup checklists',
      'Weekly self-audit checklist',
    ],
  },
  {
    id: 'ai-prompt-library',
    title: 'AI Prompt Library for Marketing',
    category: 'Tech Modules',
    format: 'PDF',
    description:
      '50+ battle-tested AI prompts organized by marketing function so you can stop staring at a blank ChatGPT window and start producing output. Every prompt has been refined through real client work across copy, social, email, SEO, and paid ads. Each section includes context-setting instructions so the AI understands your brand before it writes. Stop getting generic output — these prompts are engineered to produce work you can actually use.',
    included: [
      '50+ categorized prompts',
      'Copy and caption prompts',
      'Email subject line generators',
      'Ad headline formulas',
      'SEO meta description templates',
    ],
  },
  {
    id: 'analytics-dashboard-template',
    title: 'Analytics Dashboard Template',
    category: 'Tech Modules',
    format: 'Google Sheets',
    description:
      'A plug-and-play reporting dashboard to track your key marketing metrics across all channels in one place — no data analyst required. The KPI scorecard tab gives you a weekly snapshot of what\'s working and what\'s not, while channel performance tabs let you drill into the details. Week-over-week trend charts are pre-built so you can spot momentum shifts before they become problems. A monthly executive summary template makes reporting to stakeholders fast and professional.',
    included: [
      'KPI summary scorecard',
      'Channel performance tabs',
      'Week-over-week trend charts',
      'Goal vs. actual tracker',
      'Monthly executive summary template',
    ],
  },
  {
    id: 'paid-ads-beginner-playbook',
    title: 'Paid Ads Beginner Playbook',
    category: 'DIY Guides',
    format: 'PDF',
    description:
      'A no-fluff guide to launching your first paid ad campaign on Meta or Google, from account setup to first conversion — written for founders who have never run ads before. We walk through account structure, campaign and ad set setup, and budget allocation without assuming any prior knowledge. Creative best practices are included so your first ads don\'t look like first ads. An optimization checklist for weeks 1–4 tells you exactly what to look at and when to make changes.',
    included: [
      'Account structure walkthrough',
      'Campaign and ad set setup guide',
      'Budget allocation framework',
      'Creative best practices checklist',
      'Optimization checklist for weeks 1–4',
    ],
  },
  {
    id: 'seo-foundation-checklist',
    title: 'SEO Foundation Checklist',
    category: 'DIY Guides',
    format: 'PDF',
    description:
      'A 50-point technical and on-page SEO checklist to get your site ranking-ready before you spend a dollar on ads. Work through it yourself in a weekend and fix the issues that are silently costing you organic traffic. Covers everything from Core Web Vitals and crawlability to title tags, internal linking, and local SEO setup. Google Search Console setup instructions are included so you can start measuring results from day one.',
    included: [
      'Technical SEO audit checklist',
      'On-page optimization guide',
      'Local SEO setup steps',
      'Google Search Console setup',
      'Keyword research starter framework',
    ],
  },
  {
    id: 'email-marketing-quickstart',
    title: 'Email Marketing Quick-Start Guide',
    category: 'DIY Guides',
    format: 'PDF',
    description:
      'Everything you need to launch your first email list and automated welcome sequence in under a week — even if you\'ve never used an email platform before. We compare the top platforms by price and features so you pick the right one from the start, then walk you through list building tactics that actually grow your audience. Five ready-to-send welcome sequence templates are included so you\'re not writing from scratch. Segmentation basics and key metrics to track from day one round out the guide.',
    included: [
      'Platform comparison and setup guide',
      'List building tactics',
      'Welcome sequence templates (5 emails)',
      'Segmentation basics',
      'Key metrics to track from day one',
    ],
  },
  // ── Legal Templates ──────────────────────────────────────────────────────
  {
    id: 'free-nda-template',
    title: 'Free NDA Template',
    category: 'Legal Templates',
    format: 'Google Docs',
    description:
      'A clean, plain-English mutual non-disclosure agreement built for small businesses, freelancers, and marketing agencies. Covers confidential information shared during sales conversations, active engagements, and partnership discussions. Drafted with Clark County, Nevada governing law and adaptable to any state with a single edit. No legalese, no bloat — just the protection you need before you share anything sensitive with a prospect or vendor.',
    included: [
      'Mutual NDA (both parties protected)',
      'Definition of confidential information',
      'Exclusions and carve-outs clause',
      'Term and termination provisions',
      'Governing law placeholder (pre-filled: Clark County, NV)',
    ],
    fileUrl: null,
  },
  {
    id: 'photo-shoot-release-template',
    title: 'Photo & Video Shoot Release',
    category: 'Legal Templates',
    format: 'Google Docs',
    description:
      'A location and property release form for commercial photo and video shoots — covers both indoor and outdoor locations, private property, and branded environments. Grants the photographer, videographer, and their clients the right to use captured content for marketing, advertising, and editorial purposes. Includes a usage rights section so the scope of use is never ambiguous. Essential before any shoot where you plan to use the footage in paid ads or public-facing content.',
    included: [
      'Location and property release language',
      'Commercial and editorial usage rights',
      'Social media and digital advertising permissions',
      'Exclusivity and exclusion options',
      'Signature block for property owner and photographer',
    ],
    fileUrl: null,
  },
  {
    id: 'model-release-template',
    title: 'Model & Talent Release',
    category: 'Legal Templates',
    format: 'Google Docs',
    description:
      'A model and talent release form for any shoot involving people — employees, contractors, paid talent, or willing customers. Grants the right to use their likeness in photos, videos, and marketing materials across all channels including paid advertising. Includes a compensation acknowledgment section (works for both paid and unpaid talent) and a minor release addendum for shoots involving anyone under 18. Covers you before the shoot so there are no disputes after the edit is done.',
    included: [
      'Likeness and image usage rights',
      'Paid and unpaid talent compensation clause',
      'Digital, print, and broadcast permissions',
      'Minor release addendum (under 18)',
      'Revocation and opt-out provisions',
    ],
    fileUrl: null,
  },
  {
    id: 'social-media-content-release',
    title: 'Social Media Content Release',
    category: 'Legal Templates',
    format: 'Google Docs',
    description:
      'A UGC (user-generated content) and social media content release that lets brands legally repost, repurpose, and run ads using content created by customers, influencers, or partners. Covers Instagram, TikTok, Facebook, LinkedIn, YouTube, and any future platforms. Includes a paid amplification clause so you can boost the content as a paid ad without a separate agreement. One of the most overlooked legal documents in social media marketing — and one of the most important.',
    included: [
      'UGC repost and repurpose rights',
      'Platform-specific permissions (IG, TikTok, FB, LinkedIn, YT)',
      'Paid advertising and boosting clause',
      'Attribution and credit requirements',
      'Content modification and editing rights',
    ],
    fileUrl: null,
  },
  {
    id: 'independent-contractor-agreement',
    title: 'Independent Contractor Agreement',
    category: 'Legal Templates',
    format: 'Google Docs',
    description:
      'A comprehensive independent contractor agreement for marketing agencies, freelancers, and small businesses hiring subcontractors or project-based talent. Covers scope of work, payment terms, IP ownership (work-made-for-hire), confidentiality, and non-solicitation of clients. Drafted to clearly establish contractor (not employee) status to protect both parties from misclassification risk. Includes a statement of work addendum template so you can attach project-specific details without rewriting the master agreement each time.',
    included: [
      'Contractor vs. employee classification language',
      'Scope of work and deliverables section',
      'Work-made-for-hire IP ownership clause',
      'Confidentiality and non-solicitation provisions',
      'Statement of work addendum template',
    ],
    fileUrl: null,
  },
  {
    id: 'review-generator-spreadsheet',
    title: 'Review Generator Spreadsheet',
    category: 'Strategy Templates',
    format: 'Google Sheets',
    description:
      'The exact spreadsheet we\'ve used since 2018 to systematically generate 5-star reviews across Google, Yelp, and industry-specific platforms — without begging, without awkward asks, and without violating platform terms. Maps your customer touchpoints to the right review request moment, tracks outreach status, and logs results so you can see which channels and timing windows convert best. Used across dozens of local and national businesses to build review moats that competitors can\'t replicate overnight.',
    included: [
      'Customer touchpoint mapping worksheet',
      'Review request timing framework',
      'Multi-platform tracking (Google, Yelp, industry-specific)',
      'Outreach status tracker',
      'Review conversion rate calculator',
    ],
    fileUrl: 'https://docs.google.com/spreadsheets/d/1LFXNHHyy5-Ikx673kmFOFvJqa-oykiIYOLDB4wOQVH0/copy',
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  'Strategy Templates': 'bg-blue-950 text-blue-300 border border-blue-800',
  'Tech Modules': 'bg-purple-950 text-purple-300 border border-purple-800',
  'DIY Guides': 'bg-emerald-950 text-emerald-300 border border-emerald-800',
  'Legal Templates': 'bg-amber-950 text-amber-300 border border-amber-800',
};

const FORMAT_COLORS: Record<string, string> = {
  PDF: 'bg-[#1a0a0a] text-red-400 border border-red-900',
  'Google Sheets': 'bg-[#0a1a0a] text-green-400 border border-green-900',
  'Google Docs': 'bg-[#0a0f1a] text-sky-400 border border-sky-900',
};

// ── Testimonials Data ─────────────────────────────────────────────────────
interface Testimonial {
  name: string;
  businessType: string;
  templateUsed: string;
  quote: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Marcus T.',
    businessType: 'E-commerce Founder',
    templateUsed: '90-Day Marketing Roadmap',
    quote:
      'I downloaded the 90-Day Roadmap on a Sunday and had our entire Q3 sprint mapped out by Monday morning. By week 10 we had crossed $40K in monthly revenue for the first time. The KPI tracking rows kept us honest every single week — no more guessing what was working.',
  },
  {
    name: 'Priya S.',
    businessType: 'Wellness Brand Owner',
    templateUsed: 'Content Calendar Template',
    quote:
      'Before the Content Calendar I was posting whenever I remembered, which was basically never. Three months after using it consistently, my Instagram went from 1,200 to 6,800 followers and my email list doubled. The content pillar tracker is what made the difference — I finally had a system.',
  },
  {
    name: 'Derek L.',
    businessType: 'Marketing Consultant',
    templateUsed: 'AI Prompt Library for Marketing',
    quote:
      'I used to spend 3–4 hours writing a single client content brief. With the AI Prompt Library I cut that down to under 45 minutes. The prompts are engineered to actually produce usable output — not the generic garbage you get when you just type into ChatGPT cold.',
  },
  {
    name: 'Janelle R.',
    businessType: 'DTC Apparel Brand',
    templateUsed: 'Paid Ads Beginner Playbook',
    quote:
      'I had burned through $2,000 on Meta ads with nothing to show for it before I found this playbook. I restarted from scratch using the account structure walkthrough and the creative checklist. My first profitable campaign came in week 3 — 2.8x ROAS on a $500 test budget.',
  },
  {
    name: 'Tomás V.',
    businessType: 'B2B SaaS Startup',
    templateUsed: 'Brand Positioning Worksheet',
    quote:
      'We were about to spend $15K on a rebrand when someone sent me this worksheet. We worked through it in one afternoon and realized our positioning problem wasn\'t visual — it was our value proposition. We rewrote our homepage copy using the one-sentence formula and our demo request rate went up 34% in the first month.',
  },
  {
    name: 'Alicia M.',
    businessType: 'Local Service Business',
    templateUsed: 'SEO Foundation Checklist',
    quote:
      'I went through the entire 50-point checklist over a weekend and fixed every issue it flagged. Within 6 weeks, three of my service pages were ranking on page one for local keywords I had been invisible for. I now get 8–10 inbound calls a month from organic search alone.',
  },
];

// ── Star Rating ────────────────────────────────────────────────────────────
function StarRating() {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <svg key={i} className="w-4 h-4 text-primary" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

// ── Testimonial Card ───────────────────────────────────────────────────────
function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className="border border-[#1e1e1e] bg-[#070707] hover:border-[#2e2e2e] transition-all duration-200 p-7 flex flex-col gap-5">
      {/* Stars + Template Badge */}
      <div className="flex items-start justify-between gap-3">
        <StarRating />
        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 bg-[#0f0f0f] border border-[#2a2a2a] text-[#888] shrink-0">
          {testimonial.templateUsed}
        </span>
      </div>

      {/* Quote */}
      <blockquote className="text-[#ccc] text-sm leading-relaxed flex-1">
        &ldquo;{testimonial.quote}&rdquo;
      </blockquote>

      {/* Author */}
      <div className="pt-4 border-t border-[#141414]">
        <p className="text-white font-semibold text-sm">{testimonial.name}</p>
        <p className="text-[#666] text-xs mt-0.5">{testimonial.businessType}</p>
      </div>
    </div>
  );
}

// ── Email Gate Modal ───────────────────────────────────────────────────────
interface EmailGateModalProps {
  resource: Resource | null;
  onClose: () => void;
}

function EmailGateModal({ resource, onClose }: EmailGateModalProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  if (!resource) return null;

  async function sendDripEmail(recipientEmail: string) {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseAnonKey) throw new Error('Missing Supabase config');
    const res = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        type: 'diy_drip_day1',
        data: {
          email: recipientEmail,
          resource_name: resource!.title,
          resource_id: resource!.id,
          file_url: resource!.fileUrl || '',
        },
      }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Edge Function error ${res.status}: ${body}`);
    }
  }

  async function handleRetry() {
    setRetrying(true);
    setEmailError(false);
    try {
      await sendDripEmail(email.toLowerCase().trim());
    } catch (retryErr) {
      console.error('[DIY Drip] Retry failed:', retryErr);
      setEmailError(true);
    } finally {
      setRetrying(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('diy_downloads').insert({
        email: email.toLowerCase().trim(),
        resource_name: resource.title,
        resource_id: resource.id,
      });
      if (dbError) throw dbError;

      // Fire Day 1 drip email immediately via Edge Function
      try {
        await sendDripEmail(email.toLowerCase().trim());
      } catch (emailErr) {
        // Non-blocking: show error UI but don't fail the download gate
        console.error('[DIY Drip] Day 1 email failed:', emailErr);
        setEmailError(true);
      }

      setSuccess(true);
      // GA4: email gate submitted + download unlocked
      trackEvent('diy_download_gate_submit', {
        event_category: 'diy_templates',
        resource_title: resource.title,
        resource_category: resource.category,
        resource_format: resource.format,
      });
      trackEvent('diy_template_download', {
        event_category: 'diy_templates',
        resource_title: resource.title,
        resource_id: resource.id,
        resource_category: resource.category,
        resource_format: resource.format,
      });
      // TODO: Replace console.log with real file download trigger when file URLs are ready
      console.log(`[TODO] Trigger download for: ${resource.title}`, resource.fileUrl);
    } catch (err: unknown) {
      console.error('Download gate error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#2a2a2a] shadow-2xl">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#555] hover:text-white transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-8">
          {!success ? (
            <>
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-2 font-semibold">Free Download</p>
                <h3 className="text-xl font-bold leading-snug mb-2 text-white">{resource.title}</h3>
                <div className="flex items-center gap-2 mt-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ${FORMAT_COLORS[resource.format]}`}>
                    {resource.format}
                  </span>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-[#888] mb-2 uppercase tracking-wider">Your Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-[#111] border border-[#2a2a2a] text-white placeholder-[#444] px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors"
                    required
                    autoFocus
                  />
                  {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 text-sm uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Unlocking...' : 'Unlock Free Download'}
                </button>
                <p className="text-[11px] text-[#555] text-center">
                  No spam. Unsubscribe anytime.
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-5">
                <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Check your inbox.</h3>
              <p className="text-[#888] text-sm mb-6">
We&apos;ve sent {resource.title} to your email. Click the link inside to access your free copy.
              </p>
              {emailError && (
                <div className="mb-6 p-4 border border-red-900 bg-[#1a0808] text-left">
                  <p className="text-red-400 text-xs mb-3">
                    We couldn&apos;t send the confirmation email. Please try again or check your inbox later.
                  </p>
                  <button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="w-full border border-red-800 text-red-400 hover:bg-red-900/30 font-semibold py-2 px-4 text-xs uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {retrying ? 'Retrying...' : 'Retry Email'}
                  </button>
                </div>
              )}
              <button
                onClick={onClose}
                className="text-xs uppercase tracking-wider text-[#666] hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Resource Card ──────────────────────────────────────────────────────────
interface ResourceCardProps {
  resource: Resource;
  onDownload: (resource: Resource) => void;
}

function ResourceCard({ resource, onDownload }: ResourceCardProps) {
  return (
    <div className="border border-[#1e1e1e] bg-[#070707] hover:border-[#2e2e2e] transition-all duration-200 flex flex-col group">
      {/* Card Header */}
      <div className="p-6 pb-4 border-b border-[#141414]">
        <div className="flex items-start justify-between gap-3 mb-4">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 ${CATEGORY_COLORS[resource.category]}`}>
            {resource.category}
          </span>
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 shrink-0 ${FORMAT_COLORS[resource.format]}`}>
            {resource.format}
          </span>
        </div>
        <h3 className="text-base font-bold leading-snug group-hover:text-primary transition-colors">
          {resource.title}
        </h3>
      </div>

      {/* Description */}
      <div className="px-6 py-4 flex-1">
        <p className="text-[#888] text-sm leading-relaxed mb-5">
          {resource.description}
        </p>

        {/* What's Included */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#555] font-semibold mb-3">What's Included</p>
          <ul className="space-y-2">
            {resource.included.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-xs text-[#aaa]">
                <svg className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Download Button */}
      <div className="px-6 pb-6 pt-4">
        <button
          onClick={() => onDownload(resource)}
          className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-4 text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download Free
        </button>
      </div>
    </div>
  );
}

// ── Main DIY Content ───────────────────────────────────────────────────────
function DIYContent() {
  const searchParams = useSearchParams();
  const reason = searchParams?.get('reason');

  const [activeFilter, setActiveFilter] = useState<FilterCategory>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  const FILTER_TABS: FilterCategory[] = ['All', 'Strategy Templates', 'Tech Modules', 'DIY Guides', 'Legal Templates'];

  const filteredResources = useMemo(() => {
    return RESOURCES.filter((r) => {
      const matchesCategory = activeFilter === 'All' || r.category === activeFilter;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        r.title.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q) ||
        r.format.toLowerCase().includes(q) ||
        r.included.some((i) => i.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [activeFilter, searchQuery]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">

        {/* ── Budget Message Banner ── */}
        {reason === 'budget' && (
          <div className="bg-[#0f0a0a] border-b border-primary/20 px-6 py-5">
            <div className="max-w-[1100px] mx-auto">
              <p className="text-sm text-center">
                <span className="text-primary font-semibold">Based on your current budget, our retainer plans may not be the right fit yet</span>
                {' '}— but here are some self-service resources to get you started.
              </p>
            </div>
          </div>
        )}

        {/* ── Hero ── */}
        <section className="py-20 px-6 border-b border-[#1a1a1a]">
          <div className="max-w-[1100px] mx-auto">
            <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-4 font-semibold">Free Resource Library — No Credit Card. No Catch.</p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              14 Free Marketing Tools.<br />
              <span className="text-muted-foreground">Built by practitioners. Yours to keep.</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-[680px] mb-6">
              These are the exact frameworks, templates, and tech modules we use with paying clients — packaged for self-service and handed to you for free. No watered-down versions. No upsell traps. Just the real tools.
            </p>
            <p className="text-[#888] text-base max-w-[680px] mb-8">
              If you want to go deeper — with in-depth video walkthroughs, done-for-you setup, monthly strategy calls, and someone in your corner every step of the way — that&apos;s what <strong className="text-white">AutoPilot</strong> is for. But start here. Get the free stuff. See what we&apos;re about.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4 mt-2">
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-white">14</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">Resources</p>
              </div>
              <div className="w-px h-8 bg-[#2a2a2a] shrink-0" />
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-primary">Free</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">Always</p>
              </div>
              <div className="w-px h-8 bg-[#2a2a2a] shrink-0" />
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-white">4</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">Categories</p>
              </div>
              <div className="w-px h-8 bg-[#2a2a2a] shrink-0" />
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-white">0</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">Strings Attached</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Filter + Search Bar ── */}
        <section className="sticky top-[72px] z-30 bg-[#050505] border-b border-[#1a1a1a] px-6 py-4 overflow-hidden w-full">
          <div className="max-w-[1100px] mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Category Tabs */}
            <div className="w-full flex items-center gap-1 overflow-x-auto flex-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveFilter(tab)}
                  className={`flex-shrink-0 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    activeFilter === tab
                      ? 'bg-primary text-white' :'text-[#666] hover:text-white border border-[#2a2a2a] hover:border-[#444]'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative sm:ml-auto w-full sm:w-64">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources..."
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white placeholder-[#444] pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-[#444] transition-colors"
              />
            </div>
          </div>
        </section>

        {/* ── Resource Grid ── */}
        <section className="py-16 px-6">
          <div className="max-w-[1100px] mx-auto">
            {filteredResources.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-[#555] text-lg">No resources match your search.</p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveFilter('All'); }}
                  className="mt-4 text-xs text-primary uppercase tracking-wider hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs text-[#555] uppercase tracking-wider mb-8">
                  {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''}
                  {activeFilter !== 'All' ? ` in ${activeFilter}` : ''}
                  {searchQuery ? ` matching "${searchQuery}"` : ''}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredResources.map((resource) => (
                    <ResourceCard
                      key={resource.id}
                      resource={resource}
                      onDownload={setSelectedResource}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* ── Testimonials ── */}
        <section className="py-20 px-6 border-t border-[#1a1a1a]">
          <div className="max-w-[1100px] mx-auto">
            {/* Section Header */}
            <div className="mb-12">
              <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-4 font-semibold">Community Results</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Real Results From Real Builders.
              </h2>
              <p className="text-muted-foreground text-base max-w-[560px]">
                These aren't agency clients. These are founders and operators who downloaded the same templates you're looking at and put them to work.
              </p>
            </div>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {TESTIMONIALS.map((t) => (
                <TestimonialCard key={t.name} testimonial={t} />
              ))}
            </div>
          </div>
        </section>

        {/* ── AutoPilot CTA ── */}
        <section className="py-20 px-6 border-t border-[#1a1a1a]">
          <div className="max-w-[900px] mx-auto">
            <div className="border border-primary/30 p-10 bg-[#080808] relative overflow-hidden">
              {/* Subtle glow accent */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

              <div className="relative">
                <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-3 font-semibold">Ready to go deeper?</p>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  AutoPilot: The Paid Tier That Actually Holds Your Hand.
                </h2>
                <p className="text-[#aaa] text-base mb-6 max-w-[640px]">
                  The free templates above are real. They work. But they&apos;re the starting line, not the finish line. AutoPilot is what happens when you stop guessing and start executing with a system built behind you.
                </p>

                {/* Value stack */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">In-depth video walkthroughs</p>
                      <p className="text-[#666] text-xs">Not just a PDF — step-by-step execution with context</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Monthly strategy calls</p>
                      <p className="text-[#666] text-xs">A real expert reviewing your work and telling you what to fix</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Actionable tools &amp; done-for-you setups</p>
                      <p className="text-[#666] text-xs">Pre-configured workflows you plug in, not build from scratch</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Async Slack support</p>
                      <p className="text-[#666] text-xs">Ask questions, get answers — no more Googling for 3 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Campaign review &amp; feedback</p>
                      <p className="text-[#666] text-xs">We look at your actual work and tell you what to change</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Full modules library access</p>
                      <p className="text-[#666] text-xs">Every template, playbook, and framework we have — unlocked</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link href="/lp/autopilot">
                    <Button variant="primary" size="lg">See AutoPilot Plans — From $499/mo</Button>
                  </Link>
                  <p className="text-[#555] text-xs">
                    Cancel anytime. No long-term contracts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Come Back CTA ── */}
        <section className="py-16 px-6 border-t border-[#1a1a1a] bg-[#050505]">
          <div className="max-w-[760px] mx-auto text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4 font-semibold">When you're ready</p>
            <h2 className="text-2xl font-bold mb-4">Ready to scale with a full team?</h2>
            <p className="text-muted-foreground mb-8 max-w-[480px] mx-auto">
              When your budget and business are ready for a retainer partnership, we'd love to hear from you. Applications are reviewed within 1 business day.
            </p>
            <Link href="/book">
              <Button variant="secondary" size="lg">Apply to Work With Us</Button>
            </Link>
          </div>
        </section>

      </main>
      <Footer />

      {/* ── Email Gate Modal ── */}
      {selectedResource && (
        <EmailGateModal
          resource={selectedResource}
          onClose={() => setSelectedResource(null)}
        />
      )}
    </>
  );
}

// ── Page Export ────────────────────────────────────────────────────────────
export default function DIYPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      }
    >
      <DIYContent />
    </Suspense>
  );
}
